"use client";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useDiscountType } from "../context/DiscountTypeContext";
import Pagination, { usePagination } from '../shared/Pagination';

/* ================= MAIN PAGE ================= */

export default function DiscountType() {
  const {
  discountTypes,
  loading,
  addDiscountType,
  updateDiscountType,
  toggleStatus,
  deleteDiscountType,
} = useDiscountType();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = discountTypes.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === "status") data.sort((a, b) =>
      a.status.localeCompare(b.status)
    );

    return data;
  }, [discountTypes, search, sortBy]);

  const { page: dPage, pageSize: dSize, setPage: setDPage, setPageSize: setDSize, totalPages: dTotalPages, totalItems: dTotal, paginated: paginatedDiscount } = usePagination(filtered, 10);

  /* ---------- ACTIONS ---------- */
 const save = async () => {
  if (!name.trim()) return;

  if (editing) {
    await updateDiscountType(editing.id, name);
  } else {
    await addDiscountType(name);
  }

  setModalOpen(false);
  setEditing(null);
  setName("");
};


 const toggleStatusHandler = (id) => {
  toggleStatus(id);
};


const removeHandler = (id) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this discount type?"
  );

  if (confirmDelete) {
    deleteDiscountType(id);
  }
};

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label:'Total Types', value:discountTypes.length, accent:'border-l-teal-500', color:'text-teal-700' },
          { label:'Active',      value:discountTypes.filter(d=>d.status==='Active').length,   accent:'border-l-green-500', color:'text-green-600' },
          { label:'Inactive',    value:discountTypes.filter(d=>d.status==='Inactive').length, accent:'border-l-red-400',   color:'text-red-500' },
        ].map(({ label, value, accent, color }) => (
          <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
            <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400 leading-tight">{label}</p></div>
          </div>
        ))}
      </div>

      {/* Search & Sort */}
      <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
        <input
          placeholder="Search discount type..."
          className="flex-1 min-w-[160px] px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 outline-none cursor-pointer focus:border-teal-400 min-w-[140px]"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
        </select>
        <button onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Discount Type</span>
        </button>
      </div>

      {/* Desktop Table */}
    <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-teal-700 text-white">
  <tr>
    <th className="px-6 py-3 text-left text-sm font-semibold text-white">
      ID
    </th>

    <th className="px-6 py-3 text-left text-sm font-semibold text-white">
      Discount Type
    </th>

    <th className="px-6 py-3 text-right text-sm font-semibold text-white">
      Actions
    </th>
  </tr>
</thead>


    {/* ---------- TABLE BODY ---------- */}
  <tbody>
  {paginatedDiscount.length === 0 ? (
    <tr>
      <td colSpan={3}>
        <div className="py-20 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BadgePercent className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No discount types found</h3>
          <p className="text-sm text-gray-400 mb-5">
            {search ? 'Try adjusting your search.' : 'Get started by adding your first discount type.'}
          </p>
          {search ? (
            <button
              onClick={() => setSearch('')}
              className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Clear search
            </button>
          ) : (
            <button
              onClick={() => setModalOpen(true)}
              className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              Add Discount Type
            </button>
          )}
        </div>
      </td>
    </tr>
  ) : (
    paginatedDiscount.map((item) => (
      <tr key={item.id} className="border-t hover:bg-gray-50">
        <td className="px-4 py-2 md:table-cell md:px-6 md:py-4">
          <span>{item.id}</span>
        </td>
        <td className="px-4 py-2 md:table-cell md:px-6 md:py-4">
          <span className="font-medium">{item.name}</span>
        </td>
        <td className="px-4 py-3 md:table-cell md:px-6 md:py-4">
          <div className="flex justify-end">
            <Actions
              item={item}
              onEdit={() => { setEditing(item); setName(item.name); setModalOpen(true); }}
              onToggle={toggleStatusHandler}
              onDelete={removeHandler}
            />
          </div>
        </td>
      </tr>
    ))
  )}
</tbody>

  </table>
  <div className="px-4 border-t border-gray-100">
    <Pagination currentPage={dPage} totalPages={dTotalPages} totalItems={dTotal}
      pageSize={dSize} onPageChange={setDPage} onPageSizeChange={setDSize} />
  </div>
</div>

      {/* Mobile Cards */}
    <div className="md:hidden space-y-4">
  {paginatedDiscount.length === 0 ? (
    <div className="bg-white border border-gray-200 rounded-2xl py-20 text-center shadow-sm">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <BadgePercent className="w-8 h-8 text-gray-300" />
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-1">No discount types found</h3>
      <p className="text-sm text-gray-400 mb-5">
        {search ? 'Try adjusting your search.' : 'Get started by adding your first discount type.'}
      </p>
      {search ? (
        <button
          onClick={() => setSearch('')}
          className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Clear search
        </button>
      ) : (
        <button
          onClick={() => setModalOpen(true)}
          className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors"
        >
          Add Discount Type
        </button>
      )}
    </div>
  ) : (
    paginatedDiscount.map((item) => (
      <div key={item.id} className="bg-white rounded-xl shadow p-4 space-y-2">
        <div className="flex justify-between">
          <div>
            <p className="text-xs text-gray-500">ID</p>
            <p>{item.id}</p>
          </div>
          <Actions
            item={item}
            onEdit={() => { setEditing(item); setName(item.name); setModalOpen(true); }}
            onToggle={toggleStatusHandler}
            onDelete={removeHandler}
          />
        </div>
        <div>
          <p className="text-xs text-gray-500">Discount Type</p>
          <p className="font-medium">{item.name}</p>
        </div>
        <StatusBadge status={item.status} />
      </div>
    ))
  )}
</div>

      {/* Modal */}
     {modalOpen &&
  createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1000]" onClick={() => setModalOpen(false)} />

      {/* Side Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[1010] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white tracking-tight">
            {editing ? "Edit Discount Type" : "Add Discount Type"}
          </h2>
          <button onClick={() => setModalOpen(false)}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X size={16} className="text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Discount Type Name <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Flat Discount, Percentage Off"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={() => setModalOpen(false)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors">
            Cancel
          </button>
          <button onClick={save}
            className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm">
            {editing ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </>,
    document.body
  )}



    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
      status === "Active"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {status}
  </span>
);

const Actions = ({ item, onEdit, onToggle, onDelete }) => {
  return (
    <div className="flex items-center gap-3 justify-end">
      {/* Status Toggle */}
      <button
        onClick={() => onToggle(item.id)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
          item.status === "Active" ? "bg-green-600" : "bg-gray-300"
        }`}
        title={item.status === "Active" ? "Inactivate" : "Activate"}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            item.status === "Active" ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>

      {/* Edit */}
      <button
        onClick={onEdit}
        className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
        title="Edit"
      >
        <Edit3Icon size={16} />
      </button>

      {/* Delete */}
      <button
        onClick={() => onDelete(item.id)}
        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
        title="Delete"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};


const MenuItem = ({ children, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full px-4 py-2 text-left ${
      danger
        ? "text-red-600 hover:bg-red-50"
        : "hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);
